/* RESPONSIBLE TEAM: team-proactive-support */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/max-file-length */
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Coordinates, { type CoordinateLike } from '../coordinates';
import Bounds from '../bounds';
import { type Alignment } from '../connection-point';
import type ConnectionPoint from '../connection-point';
import type Edge from '../edge';
import type Node from '../node';

interface EdgeGenerator {
  update(inputs: GeneratorInputs): void;
  path: Path;
  pathClass: string;
  midpoint: Coordinates;
  startAndEndCoordinatesPairs: Array<[CoordinateLike, CoordinateLike]>;
  defaultToCoordinates: Array<Coordinates>;
  start: Coordinates;
  end: Coordinates;
  from: Alignment;
  to: Alignment;
}

const ARROW_HEAD_ANGLE = 210;
const DEFAULT_ARROW_LENGTH = 8;
const DEFAULT_SPACING = 1;

type ArcShape =
  | 'up-and-right'
  | 'right-and-down'
  | 'down-and-left'
  | 'left-and-up'
  | 'up-and-left'
  | 'right-and-up'
  | 'down-and-right'
  | 'left-and-down';

type LineDirection = 'up' | 'left' | 'right' | 'down' | 'diagonal';

interface GeneratorInputs {
  start: Coordinates;
  end: Coordinates;
  from?: Alignment;
  to?: Alignment;
  arrowLength?: number;
  spacing?: number;
}

class LineSegment {
  @tracked x1: number;
  @tracked y1: number;

  @tracked x2: number;
  @tracked y2: number;

  spacing = 0;

  constructor(x1: number, y1: number, x2: number, y2: number) {
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
  }

  @computed('x1', 'x2', 'y1', 'y2')
  get direction(): LineDirection {
    if (this.x1 === this.x2) {
      if (this.y1 < this.y2) {
        return 'down';
      } else {
        return 'up';
      }
    }
    if (this.y1 === this.y2) {
      if (this.x1 < this.x2) {
        return 'right';
      } else {
        return 'left';
      }
    }
    return 'diagonal';
  }

  @computed('x1', 'x2', 'y1', 'y2')
  get rotation(): number {
    let deltaX = this.x2 - this.x1;
    let deltaY = this.y2 - this.y1;
    return Math.atan2(deltaY, deltaX) / (Math.PI / 180);
  }

  @computed('rotation', 'x2', 'y2')
  get arrowHead(): string {
    let x = this.x2;
    let y = this.y2;

    let lx = x - 5 * Math.cos((this.rotation * Math.PI) / 180);
    let ly = y - 5 * Math.sin((this.rotation * Math.PI) / 180);

    return `
      M ${x} ${y}
      L ${
        x + DEFAULT_ARROW_LENGTH * Math.cos(((-ARROW_HEAD_ANGLE + this.rotation) * Math.PI) / 180)
      }
        ${
          y + DEFAULT_ARROW_LENGTH * Math.sin(((-ARROW_HEAD_ANGLE + this.rotation) * Math.PI) / 180)
        }
      L ${lx} ${ly}
      M ${x} ${y}
      L ${x + DEFAULT_ARROW_LENGTH * Math.cos(((ARROW_HEAD_ANGLE + this.rotation) * Math.PI) / 180)}
        ${y + DEFAULT_ARROW_LENGTH * Math.sin(((ARROW_HEAD_ANGLE + this.rotation) * Math.PI) / 180)}
      L ${lx} ${ly}
      `;
  }

  @computed('direction', 'spacing', 'x1', 'y1')
  get inwardArcPoint(): Coordinates {
    switch (this.direction) {
      case 'diagonal':
        return new Coordinates(this.x1, this.y1);
      case 'up':
        return new Coordinates(this.x1, this.y1 - this.spacing);
      case 'down':
        return new Coordinates(this.x1, this.y1 + this.spacing);
      case 'left':
        return new Coordinates(this.x1 - this.spacing, this.y1);
      case 'right':
        return new Coordinates(this.x1 + this.spacing, this.y1);
    }
  }

  @computed('direction', 'spacing', 'x2', 'y2')
  get outwardArcPoint(): Coordinates {
    switch (this.direction) {
      case 'diagonal':
        return new Coordinates(this.x2, this.y2);
      case 'up':
        return new Coordinates(this.x2, this.y2 + this.spacing);
      case 'down':
        return new Coordinates(this.x2, this.y2 - this.spacing);
      case 'left':
        return new Coordinates(this.x2 + this.spacing, this.y2);
      case 'right':
        return new Coordinates(this.x2 - this.spacing, this.y2);
    }
  }

  @computed('direction', 'x1', 'x2', 'y1', 'y2')
  get bounds(): Bounds {
    switch (this.direction) {
      case 'diagonal':
        return new Bounds(0, 0, 0, 0); // For a diagonal line we're just ignoring bounds overlap
      case 'up':
        return new Bounds(this.y2, this.x2, 1, Math.abs(this.y2 - this.y1));
      case 'down':
        return new Bounds(this.y1, this.x1, 1, Math.abs(this.y2 - this.y1));
      case 'left':
        return new Bounds(this.y2, this.x2, Math.abs(this.x2 - this.x1), 1);
      case 'right':
        return new Bounds(this.y1, this.x1, Math.abs(this.x2 - this.x1), 1);
    }
  }
}

class Path {
  spacing: number;
  @tracked segments: Array<LineSegment>;

  constructor(spacing: number, segments: Array<LineSegment>) {
    this.spacing = spacing;
    this.segments = segments;
    segments.forEach((segment) => (segment.spacing = spacing));
  }

  @computed('segments')
  get firstSegment(): LineSegment | undefined {
    if (this.segments) {
      return this.segments[0];
    }
    return undefined;
  }

  @computed('segments.length')
  get lastSegment(): LineSegment | undefined {
    if (this.segments) {
      return this.segments[this.segments.length - 1];
    }
    return undefined;
  }

  @computed('firstSegment', 'lastSegment', 'segments')
  get svg(): string {
    let parts: Array<string> = this.segments.reduce((parts, segment, index) => {
      if (segment === this.firstSegment) {
        parts.push(`M ${segment.x1} ${segment.y1}`);
      }
      if (segment === this.lastSegment) {
        parts.push(`L ${segment.x2} ${segment.y2}`);
      } else {
        parts.push(`L ${segment.outwardArcPoint.x} ${segment.outwardArcPoint.y}`);
        let nextSegment = this.segments[index + 1];
        let arcShape = `${segment.direction}-and-${nextSegment.direction}` as ArcShape;
        parts.push(this.arc(arcShape));
      }
      return parts;
    }, [] as Array<string>);
    return parts.join('\n');
  }

  @computed('lastSegment.arrowHead')
  get arrowHead(): string {
    if (this.lastSegment) {
      return this.lastSegment.arrowHead;
    } else {
      return ``;
    }
  }

  overlaps(nodes: Array<Node>) {
    return nodes.some((node) => {
      return this.segments.some((segment) => {
        return segment.bounds.overlaps(node.bounds);
      });
    });
  }

  private arc(direction: ArcShape): string {
    switch (direction) {
      case 'up-and-right':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 1, ${this.spacing}, ${-this.spacing}`;
      case 'up-and-left':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 0, ${-this.spacing}, ${-this.spacing}`;
      case 'down-and-left':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 1, ${-this.spacing}, ${this.spacing}`;
      case 'down-and-right':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 0, ${this.spacing}, ${this.spacing}`;
      case 'right-and-down':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 1, ${this.spacing}, ${this.spacing}`;
      case 'right-and-up':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 0, ${this.spacing}, ${-this.spacing}`;
      case 'left-and-up':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 1, ${-this.spacing}, ${-this.spacing}`;
      case 'left-and-down':
        return `a ${this.spacing} ${this.spacing}, 0, 0, 0, ${-this.spacing}, ${this.spacing}`;
    }
  }
}

export default class StandardEdgeGenerator implements EdgeGenerator {
  @tracked start: Coordinates = new Coordinates(0, 0);
  @tracked end: Coordinates = new Coordinates(0, 0);
  @tracked from: Alignment = 'left';
  @tracked to: Alignment = 'right';
  arrowLength: number = DEFAULT_ARROW_LENGTH;
  @tracked spacing = 1;
  @tracked edge: Edge;

  constructor(edge: Edge) {
    this.edge = edge;
  }

  update(inputs: GeneratorInputs) {
    this.start = inputs.start;
    this.end = inputs.end;
    this.from = inputs.from ?? 'left';
    this.to = inputs.to ?? 'right';
    this.arrowLength = inputs.arrowLength ?? DEFAULT_ARROW_LENGTH;
    this.spacing = inputs.spacing ?? DEFAULT_SPACING;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'defaultToCoordinates',
    'edge.predecessor.connectionPoints',
    'edge.successor.connectionPoints',
    'fromConnectionPoints',
    'toConnectionPoints',
  )
  get startAndEndCoordinatesPairs(): Array<[CoordinateLike, CoordinateLike]> {
    if (
      isEmpty(this.edge.predecessor.connectionPoints) ||
      (this.edge.successor && isEmpty(this.edge.successor?.connectionPoints))
    ) {
      throw new Error(`A Node must have connection points specified for Edges to connect to`);
    }
    let fromCoordinates: Array<CoordinateLike> = this.fromConnectionPoints;
    let toCoordinates: Array<CoordinateLike> = this.toConnectionPoints;
    if (isEmpty(toCoordinates)) {
      toCoordinates = this.defaultToCoordinates;
    }
    return Coordinates.coordinatePairsByDistance(fromCoordinates, toCoordinates);
  }

  @computed('edge.predecessor.connectionPoints.@each.coordinates')
  get fromConnectionPoints(): Array<ConnectionPoint> {
    return this.edge.predecessor.connectionPoints;
  }

  @computed('edge.successor.connectionPoints.@each.coordinates')
  get toConnectionPoints(): Array<ConnectionPoint> {
    return this.edge.successor?.connectionPoints || [];
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'edge.graph.hooks',
    'edge.graph.state.hoveredNodes.[]',
    'edge.graph.state.mouseCoordinates',
    'edge.predecessor.graph.state.hoveredNodes.length',
  )
  get defaultToCoordinates(): Array<Coordinates> {
    if (
      this.edge.predecessor.graph.state.hoveredNodes.length &&
      this.edge.graph.hooks.canInsertEdge(
        this.edge.predecessor,
        this.edge.graph.state.hoveredNodes[0],
      )
    ) {
      return this.edge.graph.state.hoveredNodes[0].connectionPoints
        .filter((cp) => cp.direction === 'inwards' || cp.direction === 'both')
        .map((cp) => cp.coordinates);
    } else {
      return [this.edge.graph.state.mouseCoordinates];
    }
  }

  get pathClass(): string {
    return 'graph__edge__default-styles';
  }

  get path(): Path {
    if (this.to === 'no-alignment') {
      return this.straightLine();
    }

    if (this.from === 'top' && this.to === 'top') {
      return this.topToTop();
    }

    if (
      Math.abs(this.start.x - this.end.x) < this.spacing ||
      Math.abs(this.start.y - this.end.y) < this.spacing
    ) {
      return this.straightLine();
    }

    if (this.from === 'right' && this.to === 'left') {
      return this.rightToLeft();
    }

    if (this.from === 'right' && this.to === 'bottom' && this.start.y > this.end.y) {
      return this.rightToBottom();
    }

    if (this.from === 'right' && this.to === 'top' && this.start.y < this.end.y) {
      return this.rightToTop();
    }

    if ((this.from === 'bottom' || this.from === 'top') && this.to === 'left') {
      return this.verticalToHorizontal();
    }

    if (
      (this.from === 'top' && this.to === 'bottom' && this.start.y > this.end.y) ||
      (this.from === 'bottom' && this.to === 'top' && this.start.y < this.end.y)
    ) {
      return this.verticalPath();
    }

    return this.straightLine();
  }

  protected pathFromCoordinateList(coordinates: Array<[number, number]>): Path {
    let segments = [] as Array<LineSegment>;
    coordinates.forEach((coordinate, index) => {
      if (index + 1 < coordinates.length) {
        let nextCoordinate = coordinates[index + 1];
        segments.push(
          new LineSegment(coordinate[0], coordinate[1], nextCoordinate[0], nextCoordinate[1]),
        );
      }
    });
    return new Path(this.spacing, segments);
  }

  protected rightToLeft(): Path {
    return this.pathFromCoordinateList([
      [this.start.x, this.start.y],
      [this.midpoint.x, this.start.y],
      [this.midpoint.x, this.end.y],
      [this.end.x, this.end.y],
    ]);
  }

  private rightToBottom(): Path {
    if (this.start.y - this.end.y < this.spacing * 2) {
      return this.straightLine();
    }

    if (this.start.x > this.end.x) {
      let midY = (this.start.y - this.end.y) / 2;
      let absCurveStartY = this.start.y - midY;

      return this.pathFromCoordinateList([
        [this.start.x, this.start.y],
        [this.start.x + this.spacing, this.start.y],
        [this.start.x + this.spacing, absCurveStartY],
        [this.midpoint.x, this.end.y],
        [this.end.x, this.end.y],
      ]);
    } else {
      return this.pathFromCoordinateList([
        [this.start.x, this.start.y],
        [this.end.x, this.start.y],
        [this.end.x, this.end.y],
      ]);
    }
  }

  private rightToTop(): Path {
    if (this.end.y - this.start.y < this.spacing * 2) {
      return this.straightLine();
    }

    if (this.start.x > this.end.x) {
      let midY = (this.end.y - this.start.y) / 2;
      let absCurveStartY = this.start.y + midY;

      return this.pathFromCoordinateList([
        [this.start.x, this.start.y],
        [this.start.x + this.spacing, this.start.y],
        [this.start.x + this.spacing, absCurveStartY],
        [this.end.x, absCurveStartY],
        [this.end.x, this.end.y],
      ]);
    } else {
      return this.pathFromCoordinateList([
        [this.start.x, this.start.y],
        [this.end.x, this.start.y],
        [this.end.x, this.end.y],
      ]);
    }
  }

  private topToTop(): Path {
    let minY = Math.min(this.start.y, this.end.y) - this.spacing * 2;
    return this.pathFromCoordinateList([
      [this.start.x, this.start.y],
      [this.start.x, minY],
      [this.end.x, minY],
      [this.end.x, this.end.y],
    ]);
  }

  private verticalToHorizontal(): Path {
    return this.pathFromCoordinateList([
      [this.start.x, this.start.y],
      [this.start.x, this.end.y],
      [this.end.x, this.end.y],
    ]);
  }

  private verticalPath(): Path {
    if (this.start.x === this.end.x) {
      return this.straightLine();
    }

    return this.pathFromCoordinateList([
      [this.start.x, this.start.y],
      [this.start.x, this.midpoint.y],
      [this.end.x, this.midpoint.y],
      [this.end.x, this.end.y],
    ]);
  }

  protected straightLine(): Path {
    return this.pathFromCoordinateList([
      [this.start.x, this.start.y],
      [this.end.x, this.end.y],
    ]);
  }

  @computed('end.{x,y}', 'from', 'spacing', 'start.{x,y}', 'to')
  get midpoint(): Coordinates {
    let x = Math.min(this.start.x, this.end.x) + Math.abs(this.start.x - this.end.x) / 2;
    let y = Math.min(this.start.y, this.end.y) + Math.abs(this.start.y - this.end.y) / 2;

    let midpoint = new Coordinates(x, y);

    if (this.from === 'right' && this.to === 'bottom' && this.start.x < this.end.x) {
      midpoint.y = this.start.y;
    }

    if (this.from === 'right' && this.to === 'top' && this.start.x < this.end.x) {
      midpoint.y = this.start.y;
    }

    if (this.from === 'top' && this.to === 'left') {
      midpoint.y = this.end.y;
    }

    if (this.from === 'bottom' && this.to === 'left') {
      midpoint.y = this.end.y;
    }

    if (this.from === 'top' && this.to === 'top') {
      midpoint.y -= this.spacing * 2;
    }

    return midpoint;
  }

  get rotation(): number {
    if (this.from === 'bottom') {
      return 90;
    }

    if (this.from === 'right') {
      return 0;
    }

    if (this.from === 'top') {
      return 270;
    }

    let deltaX = this.end.x - this.start.x;
    let deltaY = this.end.y - this.start.y;
    return Math.atan2(deltaY, deltaX) / (Math.PI / 180);
  }
}

export { EdgeGenerator, LineSegment, Path };
